import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Spinner, Tabs, Tab } from 'react-bootstrap';
import Filter from './components/filter/filter';
import ReportContainer from './components/report-container/report-container';
import AuditConfirmationContainer from './components/audit-confirmation-container/audit-confirmation-container';
import CertificationOfHoldingsContainer from './components/certification-of-holdings-container/certification-of-holdings-container';

import { useNavigate } from 'react-router-dom';
import ReportsService from '../../../../services/reports-service';

import './maker-reports-module.scss';
import { IdentityContext } from '../../../../context/identity-context';

const MakerReportsModule = () => {
  const [items, setItems] = useState([]);
  const [auditConfirmations, setAuditConfirmations] = useState([]);
  const [certificationHoldings, setCertificationHoldings] = useState([]);

  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredAuditConfirmations, setFilteredAuditConfirmations] = useState([]);
  const [filteredCertificationHoldings, setFilteredCertificationHoldings] = useState([]);

  const [filtersOutstanding, setFiltersOutstanding] = useState({ status: [], issuers: [] });
  const [filtersAudit, setFiltersAudit] = useState({ status: [], issuers: [] });
  const [filtersCert, setFiltersCert] = useState({ status: [], issuers: [] });

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('outstanding');

  const reportsService = useMemo(() => new ReportsService(), []);
  const navigate = useNavigate();
  const { user } = useContext(IdentityContext);

  useEffect(() => {
    let isMounted = true;
    const getReports = async () => {
      try {
        const result = await reportsService.getReports('rr_maker', user.attributes);
        if (!isMounted) return;
        setItems(result.items);
        setFilteredItems(result.items);
        setAuditConfirmations(result.auditConfirmations);
        setFilteredAuditConfirmations(result.auditConfirmations);
        setCertificationHoldings(result.certificationHoldings);
        setFilteredCertificationHoldings(result.certificationHoldings);
      } catch (error) {
        console.error('Fetching error:', error);
        navigate('/internal-server-error');
      } finally {
        isMounted && setLoading(false);
      }
    };
    getReports();
    return () => { isMounted = false; };
  }, [reportsService, user.attributes, navigate]);

  // Unified filter application
  const applyFilters = useCallback((tab, filters) => {
    const { status, issuers } = filters;
    if (tab === 'outstanding') {
      setFilteredItems(
        items.filter(item =>
          (status.length === 0 || status.includes(item.status)) &&
          (issuers.length === 0 || issuers.includes(item.securityIssuer))
        )
      );
    }
    if (tab === 'audit') {
      setFilteredAuditConfirmations(
        auditConfirmations.filter(conf =>
          (status.length === 0 || status.includes(conf.status)) &&
          (issuers.length === 0 || issuers.includes(conf.issuerName))
        )
      );
    }
    if (tab === 'certification') {
      setFilteredCertificationHoldings(
        certificationHoldings.filter(cert =>
          (status.length === 0 || status.includes(cert.status)) &&
          (issuers.length === 0 || issuers.includes(cert.bpId))
        )
      );
    }
  }, [items, auditConfirmations, certificationHoldings]);

  const handleFilterChange = (newFilters) => {
    if (activeTab === 'outstanding') {
      setFiltersOutstanding(newFilters);
      applyFilters('outstanding', newFilters);
    }
    if (activeTab === 'audit') {
      setFiltersAudit(newFilters);
      applyFilters('audit', newFilters);
    }
    if (activeTab === 'certification') {
      setFiltersCert(newFilters);
      applyFilters('certification', newFilters);
    }
  };

  // Determine source items and current filters
  const [sourceItems, currentFilters] = useMemo(() => {
    switch (activeTab) {
      case 'audit':
        return [auditConfirmations, filtersAudit];
      case 'certification':
        return [certificationHoldings, filtersCert];
      default:
        return [items, filtersOutstanding];
    }
  }, [activeTab, items, auditConfirmations, certificationHoldings, filtersOutstanding, filtersAudit, filtersCert]);

  // Normalize items for the Filter component
  const normalizedItems = useMemo(() => {
    return sourceItems.map(item => {
      switch (activeTab) {
        case 'audit':
          return { status: item.status, issuers: item.issuerName };
        case 'certification':
          return { status: item.status, issuers: item.bpId };
        default:
          return { status: item.status, issuers: item.securityIssuer };
      }
    });
  }, [sourceItems, activeTab]);

  // Define filter fields
  const filterFields = useMemo(() => [
    { key: 'status', label: 'Status', type: 'checkbox' },
    { key: 'issuers', label: 'Issuer', type: 'checkbox' }
  ], []);

  return (
    <Container fluid className="maker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <Tabs
            activeKey={activeTab}
            onSelect={tab => {
              setActiveTab(tab);
              const reset = { status: [], issuers: [] };
              if (tab === 'outstanding') setFiltersOutstanding(reset);
              if (tab === 'audit') setFiltersAudit(reset);
              if (tab === 'certification') setFiltersCert(reset);
              applyFilters(tab, reset);
            }}
            className="mb-3"
          >
            <Tab eventKey="outstanding" title="Outstanding Balances" />
            <Tab eventKey="audit" title="Audit Confirmation" />
            <Tab eventKey="certification" title="Certification of Holdings" />
          </Tabs>
          <Row>
            <Col xs={12} md={3} className="panel">
              <Filter
                key={activeTab}
                items={normalizedItems}
                filter={currentFilters}
                onFilterChange={handleFilterChange}
                fields={filterFields}
              />
            </Col>
            <Col xs={12} md={9}>
              {activeTab === 'outstanding' && <ReportContainer items={filteredItems} />}
              {activeTab === 'audit' && <AuditConfirmationContainer items={filteredAuditConfirmations} />}
              {activeTab === 'certification' && <CertificationOfHoldingsContainer items={filteredCertificationHoldings} />}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default MakerReportsModule;
